import { InvoiceCycleModel } from '../../api/Argentia/endpoints/invoiceCycle/model/invoiceCycle'
import {
  apiCloseExecutionCycle,
  apiCreateInvoiceCycle,
  apiDeleteInvoiceCycle,
  apiEditInvoiceCycle,
  apiGetActiveInvoiceCyclesExecutions,
  apiGetAnnulmentCycleExecution,
  apiGetAnnulmentCycleStatus,
  apiGetAvailableInvoiceCycles,
  apiGetFinishedExecutionsByInvoiceCycleId,
  apiGetGlobalInvoiceCycleObjections,
  apiGetInvoiceCycleCompatibilities,
  apiGetInvoiceCycleExecutionByID,
  apiGetInvoiceCycleProcessing,
  apiGetInvoiceCyclesCount,
  apiGetInvoiceCyclesExecutions,
  apiGetReinvoiceCycleExecution,
  apiGetReinvoiceCycleStatus,
  apiGetTotalInvoiceCycles,
  apiPatchInvoiceCyclesExecution,
  apiPostInvoiceCyclesExecution
} from '../../api/Argentia/endpoints/invoiceCyclesExecutions/invoiceCyclesExecutions.api'
import {
  AnnulmentCycleExecutionModel,
  AnnulmentCyclesListModel,
  ReinvoiceCycleExecutionModel,
  ReinvoiceCyclesListModel
} from '../../api/Argentia/endpoints/invoiceCyclesExecutions/model/annulmentCycleStatus'
import {
  cfExecutionModel,
  cfExecutionPatchRequestModel,
  cfExecutionRequestModel,
  CloseExecutionCycleRequestModel,
  GlobalInvoiceCycleObjectionsTableModel,
  InvoiceCycleCountResponseModel,
  InvoiceCycleProcessingModel,
  InvoiceCycleWithLastExecutionModel
} from '../../api/Argentia/endpoints/invoiceCyclesExecutions/model/cfExecutionModel'
import {
  CreateInvoiceCycleRequest,
  UpdateInvoiceCycleRequest
} from '../../api/Argentia/endpoints/invoiceCyclesExecutions/model/createInvoiceCycleRequest'
import { InvoiceCycleCompatibilities } from '../../api/Argentia/endpoints/invoiceCyclesExecutions/model/invoiceCycleCompatibilities'
import { ReportIdResponse } from '../../api/Argentia/endpoints/reports/model/reports'
import { apiFetchReportId } from '../../api/Argentia/endpoints/reports/reports.api'
import {
  createErrorServiceResponse,
  createSuccessServiceResponse
} from '../../helpers/serviceResponseHelper'
import { ApiError } from '../../model/apiError'
import { ServiceResponse } from '../../model/serviceResponse'
import {
  cfExecutionModelListToCycleExecutionModelWithCycleList,
  cfExecutionTableModelToCycleExecutionModel
} from './invoiceCycles.mapper'
import { CycleExecutionModel, CycleExecutionWithInvoiceCycleModel } from './invoiceCycles.model'

export const fetchTotalInvoiceCycles = async (): Promise<
  ServiceResponse<InvoiceCycleModel[] | undefined>
> => {
  return await apiGetTotalInvoiceCycles()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchInvoiceCyclesExecutions = async (
  subjectType?: number,
  activesOnly?: boolean,
  commodity?: number
): Promise<ServiceResponse<InvoiceCycleWithLastExecutionModel[] | undefined>> => {
  return await apiGetInvoiceCyclesExecutions(subjectType, activesOnly, commodity)
    .then(response => createSuccessServiceResponse(response))
    .catch(error => createErrorServiceResponse(error))
}

export const fetchAvailableInvoiceCycles = async (): Promise<
  ServiceResponse<InvoiceCycleModel[] | undefined>
> => {
  return await apiGetAvailableInvoiceCycles()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchActiveInvoiceCyclesExecutions = async (): Promise<
  ServiceResponse<CycleExecutionWithInvoiceCycleModel[] | undefined>
> => {
  return await apiGetActiveInvoiceCyclesExecutions()
    .then(response => {
      return createSuccessServiceResponse(
        cfExecutionModelListToCycleExecutionModelWithCycleList(response)
      )
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const createInvoiceCycleExecution = async (
  request: cfExecutionRequestModel
): Promise<ServiceResponse<cfExecutionModel | undefined>> => {
  return await apiPostInvoiceCyclesExecution(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updateInvoiceCycleExecution = async (
  executionCycleId: number,
  request: cfExecutionPatchRequestModel
): Promise<ServiceResponse<cfExecutionModel | undefined>> => {
  return await apiPatchInvoiceCyclesExecution(executionCycleId, request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchInvoiceCyclesCount = async (): Promise<
  ServiceResponse<InvoiceCycleCountResponseModel | undefined>
> => {
  return await apiGetInvoiceCyclesCount()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchInvoiceCycleCompatibilities = async (): Promise<
  ServiceResponse<InvoiceCycleCompatibilities[] | undefined>
> => {
  return await apiGetInvoiceCycleCompatibilities()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const createInvoiceCycle = async (
  request: CreateInvoiceCycleRequest
): Promise<ServiceResponse<InvoiceCycleModel | undefined>> => {
  return await apiCreateInvoiceCycle(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const editInvoiceCycle = async (
  request: UpdateInvoiceCycleRequest
): Promise<ServiceResponse<InvoiceCycleModel | undefined>> => {
  return await apiEditInvoiceCycle(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const deleteInvoiceCycle = async (
  invoiceCycleId: number
): Promise<ServiceResponse<void | undefined>> => {
  return await apiDeleteInvoiceCycle(invoiceCycleId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchInvoiceCycleExecution = async (
  executionCycleId: number
): Promise<ServiceResponse<CycleExecutionModel | undefined>> => {
  try {
    const response = await apiGetInvoiceCycleExecutionByID(executionCycleId)
    const mappedData = cfExecutionTableModelToCycleExecutionModel(response)
    return createSuccessServiceResponse(mappedData)
  } catch (error) {
    return createErrorServiceResponse(error as ApiError)
  }
}

export const fetchInvoiceCycleProcessing = async (
  executionCycleId: number
): Promise<ServiceResponse<InvoiceCycleProcessingModel | undefined>> => {
  return await apiGetInvoiceCycleProcessing(executionCycleId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchGlobalInvoiceCycleObjections = async (
  executionCycleId: number
): Promise<ServiceResponse<GlobalInvoiceCycleObjectionsTableModel[] | undefined>> => {
  return await apiGetGlobalInvoiceCycleObjections(executionCycleId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchFinishedExecutionsByInvoiceCycleId = async (
  invoiceCycleId: number
): Promise<ServiceResponse<CycleExecutionModel[] | undefined>> => {
  return await apiGetFinishedExecutionsByInvoiceCycleId(invoiceCycleId)
    .then(response => {
      return createSuccessServiceResponse(
        cfExecutionModelListToCycleExecutionModelWithCycleList(response)
      )
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const closeExecutionCycle = async (
  request: CloseExecutionCycleRequestModel
): Promise<ServiceResponse<void | undefined>> => {
  return await apiCloseExecutionCycle(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getReportId = async (
  tipoSujeto: number,
  tipoFacturacion: number,
  tipoCiclo: number,
  commodity: number = 1
): Promise<ServiceResponse<ReportIdResponse | undefined>> => {
  return await apiFetchReportId(tipoSujeto, tipoFacturacion, tipoCiclo, commodity)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getAnnulmentCycleStatus = async (
  subjectType: number
): Promise<ServiceResponse<AnnulmentCyclesListModel | undefined>> => {
  return await apiGetAnnulmentCycleStatus(subjectType)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getAnnulmentCycleExecution = async (
  invoiceCycleId: number
): Promise<ServiceResponse<AnnulmentCycleExecutionModel | null | undefined>> => {
  return await apiGetAnnulmentCycleExecution(invoiceCycleId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getReinvoiceCycleStatus = async (
  subjectType: number
): Promise<ServiceResponse<ReinvoiceCyclesListModel | undefined>> => {
  return await apiGetReinvoiceCycleStatus(subjectType)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getReinvoiceCycleExecution = async (
  invoiceCycleId: number
): Promise<ServiceResponse<ReinvoiceCycleExecutionModel | null | undefined>> => {
  return await apiGetReinvoiceCycleExecution(invoiceCycleId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}
